import {
  CircularProgressWithLabel,
  FCheckbox,
  Iconify,
  LoadingContainer,
  Text,
  useFileUrlToTypeMapping,
} from '@pflegenavi/web-components';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import type { Theme } from '@mui/material/styles';
import {
  Card,
  FormControlLabel,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import {
  getReceiptImagesKey,
  useReceiptImages,
  useUploadReceiptImage,
} from '@pflegenavi/frontend/api-nursing-home';
import type { FormikProps } from 'formik';
import { v4 } from 'uuid';
import { ImagesHandlingForm } from './ImagesHandlingForm';
import { HiddenInputForReceiptFile } from './HiddenInputForReceiptFile';
import type { ConnectDropTarget } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import {
  resolveFileType,
  type UploadFile,
  type UploadState,
} from '@pflegenavi/shared-frontend/platform';
import { computeMd5HashOfFile } from '@pflegenavi/shared-web/hash-utils';
import { rotateImage } from './rotateImage';

interface UploadReceiptCardProps<
  V extends { receiptImageIds: string[]; receipt_not_available: boolean }
> {
  formik: FormikProps<V>;
  disabled?: boolean;
  uploadReceipt: UploadReceiptHookReturn;
  allowWithoutImage?: boolean;
  /**
   * Where the preview urls come from, keyed by image id. Defaults to the
   * receipt bucket (`useReceiptImages`). Pass this for images stored elsewhere,
   * e.g. the SEPA document bucket, which has no presign-by-id endpoint and
   * previews the picked files through object urls instead.
   */
  imageUrls?: {
    data?: Record<string, string | undefined>;
    /** Filenames keyed by image id, for sources with no presign-by-id
     * endpoint whose urls can't carry a Content-Disposition header. */
    names?: Record<string, string | undefined>;
    isLoading?: boolean;
    isError?: boolean;
  };
  /** Overrides the receipt wording for other kinds of documents. */
  uploadLabel?: string;
  /** Tooltip on the "add another" button; defaults to the receipt wording. */
  addFileLabel?: string;
  /**
   * Render PDF receipts inline (ENG-2295). Pass `false` where the preview sits
   * in a narrow column and an A4 page would be too small to read - the icon
   * placeholder plus the enlarge dialog serves better there.
   */
  inlinePdfPreview?: boolean;
}

export type FilePreviewType =
  | {
      preview?: string | null;
      name?: string;
    }
  | undefined;

export const UploadReceiptCard = <
  V extends {
    receiptImageIds: string[];
    receipt_not_available: boolean;
  }
>({
  formik,
  disabled = true,
  uploadReceipt,
  allowWithoutImage = true,
  inlinePdfPreview,
  imageUrls,
  uploadLabel,
  addFileLabel,
}: UploadReceiptCardProps<V>): JSX.Element => {
  const inputRef = createRef<HTMLInputElement>();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const receiptImageIds = formik.values['receiptImageIds'];

  // Passing undefined disables the query, so an overridden url source costs no
  // request against the receipt bucket.
  const receiptImagesQuery = useReceiptImages(
    imageUrls ? undefined : receiptImageIds
  );
  const {
    data: receiptImageUrlsMap,
    isLoading,
    isError,
  } = imageUrls ?? receiptImagesQuery;

  // Driven by receiptImageIds (not just the map's own keys) so a deleted id
  // disappears immediately, even when the url source is an append-only map
  // that outlives individual deletions (e.g. the SEPA document previews).
  const receiptImageUrls: string[] | undefined = useMemo(() => {
    if (receiptImageUrlsMap) {
      return receiptImageIds
        .map((id) => receiptImageUrlsMap[id])
        .filter((item): item is string => item !== undefined);
    }
    return undefined;
  }, [receiptImageUrlsMap, receiptImageIds]);

  // Ids for the entries actually shown (some ids can have no resolved url,
  // e.g. a failed presigned url - see `PresignedUrl.generate_download_urls`),
  // kept in the same order/filter as `receiptImageUrls` so a thumbnail's
  // index maps to the right id on delete.
  const visibleReceiptImageIds = useMemo(() => {
    if (!receiptImageUrlsMap) {
      return receiptImageIds;
    }
    return receiptImageIds.filter(
      (id) => receiptImageUrlsMap[id] !== undefined
    );
  }, [receiptImageIds, receiptImageUrlsMap]);

  const urlToName = useMemo(() => {
    if (!imageUrls?.names || !receiptImageUrlsMap) {
      return undefined;
    }
    const map: Record<string, string> = {};
    for (const id of receiptImageIds) {
      const url = receiptImageUrlsMap[id];
      const name = imageUrls.names[id];
      if (url && name) {
        map[url] = name;
      }
    }
    return map;
  }, [imageUrls?.names, receiptImageUrlsMap, receiptImageIds]);

  const imageUrlToTypeMap = useFileUrlToTypeMapping(
    receiptImageUrls,
    urlToName
  );

  const onOpenSelectImageDialog = useCallback(() => {
    inputRef?.current?.click();
  }, [inputRef]);

  const { setFieldError, setFieldValue, setSubmitting } = formik;

  const { uploadState, canDrop, isOver, setFile, file, upload } = uploadReceipt;

  // `uploadState` stays `finished` forever and lives in the caller's
  // useUploadReceipt, which outlives this card (MUI Dialog unmounts its
  // children on close). Treat an upload that was already finished at mount as
  // consumed, so reopening the dialog can't append the previous receipt's
  // image to the next one.
  const appliedUpload = useRef(uploadState?.finished ? uploadState : undefined);
  // Original file and how many quarter-turns it has been rotated, per image
  // id, so every rotation renders from the source instead of the previous
  // (lossy) copy.
  const rotations = useRef(
    new Map<string, { source: Blob; name: string; turns: number }>()
  );

  useEffect(() => {
    if (uploadState?.error) {
      setFieldError('receiptImageIds', 'Failed to upload receipt');
      setSubmitting(false);
    }
  }, [uploadState?.error, setFieldError, setSubmitting]);

  useEffect(() => {
    if (!uploadState?.finished || appliedUpload.current === uploadState) {
      return;
    }
    appliedUpload.current = uploadState;
    if (receiptImageIds.includes(uploadState.receiptImageId ?? '')) {
      // A rotated copy overwrote the object under its existing id, so the
      // cached presigned url would still show the old bytes.
      void queryClient.invalidateQueries({
        queryKey: getReceiptImagesKey(receiptImageIds.toString()),
      });
    } else {
      setFieldValue('receiptImageIds', [
        ...receiptImageIds,
        uploadState.receiptImageId,
      ]);
      setSelectedImageIndex(receiptImageIds.length);
    }
    setSubmitting(false);
    // TODO many dependencies missing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadState, setFieldValue, setSubmitting]);

  const onDeleteFile = useCallback(
    (index: number) => {
      const idToDelete = visibleReceiptImageIds[index];
      setFieldValue(
        'receiptImageIds',
        receiptImageIds.filter((id) => id !== idToDelete)
      );
      setSelectedImageIndex(0);
    },
    [receiptImageIds, visibleReceiptImageIds, setFieldValue]
  );

  // Re-uploads under the same id: the object is overwritten in place, so the
  // form value, selection and any analysis already run on it stay as they are.
  const onRotateFile = useCallback(
    async (index: number) => {
      const url = receiptImageUrls?.[index];
      const entry = url === undefined ? undefined : imageUrlToTypeMap.get(url);
      const id = visibleReceiptImageIds[index];
      if (!entry?.type.startsWith('image/') || id === undefined) {
        return;
      }
      try {
        const prev = rotations.current.get(id) ?? {
          source: await entry.data,
          name: entry.name,
          turns: 0,
        };
        const next = { ...prev, turns: (prev.turns + 1) % 4 };
        const rotated = await rotateImage(next.source, next.name, next.turns);
        rotations.current.set(id, next);
        formik.setSubmitting(true);
        upload(rotated, rotated.type, undefined, id);
      } catch {
        enqueueSnackbar(t('receipt.upload.rotate-failed'), {
          variant: 'error',
        });
      }
    },
    [
      receiptImageUrls,
      imageUrlToTypeMap,
      visibleReceiptImageIds,
      formik,
      upload,
      enqueueSnackbar,
      t,
    ]
  );

  const onSelectDifferentImage = (imageUrl: string) => {
    const imageIndex = receiptImageUrls?.indexOf(imageUrl);
    if (imageIndex !== undefined && imageIndex !== -1) {
      setSelectedImageIndex(imageIndex);
    }
  };

  const onAddFile = useCallback(() => {
    const file = inputRef?.current?.files?.[0];

    if (!file) {
      return;
    }

    const fileType = resolveFileType(file);
    if (!fileType) {
      enqueueSnackbar(t('receipt.upload.unsupported-file-type'), {
        variant: 'error',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      const preview = e?.target?.result;
      if (preview instanceof ArrayBuffer) {
        throw new Error('Unexpected array buffer');
      }
      setFile({ preview, name: file.name });
    };

    formik.setSubmitting(true);
    upload(file, fileType);
    reader.readAsDataURL(file);
  }, [formik, inputRef, upload, setFile, enqueueSnackbar, t]);

  const isActive = canDrop && isOver;

  if (isError) {
    return <Typography>{t('receipt.failed-to-load-images')}</Typography>;
  }

  if (uploadState !== undefined && !uploadState.finished) {
    return (
      <FileChosenContainer center={true} sx={{ justifyContent: 'center' }}>
        <CircularProgressWithLabel
          value={uploadState?.progress ? uploadState?.progress * 100 : 0}
          color="secondary"
          size={120}
        />
      </FileChosenContainer>
    );
  }

  if (receiptImageUrls && receiptImageUrls?.length > 0) {
    return (
      <FileChosenContainer>
        <ImagesHandlingForm
          canEdit={!disabled}
          addFileLabel={addFileLabel}
          inlinePdfPreview={inlinePdfPreview}
          file={file}
          receiptImageUrls={receiptImageUrls}
          imageUrlToTypeMap={imageUrlToTypeMap}
          inputRef={inputRef}
          onAddFile={onAddFile}
          onOpenSelectImageDialog={onOpenSelectImageDialog}
          onDeleteFile={onDeleteFile}
          onRotateFile={imageUrls ? undefined : onRotateFile}
          selectedImageIndex={selectedImageIndex}
          onSelectDifferentImage={onSelectDifferentImage}
        />
      </FileChosenContainer>
    );
  }

  return (
    <Card sx={{ p: 3, flex: 1 }}>
      {isLoading && receiptImageIds.length !== 0 ? (
        <LoadingContainer height={200} />
      ) : (
        <Stack direction="column" sx={{ height: 1, justifyContent: 'center' }}>
          <RootStyle>
            <HiddenInputForReceiptFile
              onChangeFile={onAddFile}
              inputRef={inputRef}
            />
            <DropZoneStyle
              onClick={disabled ? undefined : onOpenSelectImageDialog}
              sx={{
                ...(isActive && { opacity: 0.72 }),
                ...(disabled && {
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  '&:hover': { cursor: 'not-allowed' },
                }),
              }}
            >
              <PlaceholderStyle
                className="placeholder"
                hide={receiptImageIds.length !== 0}
              >
                <Iconify
                  icon={'ic:round-add-a-photo'}
                  sx={{ width: 24, height: 24, mb: 1 }}
                />
                <Text variant="caption">
                  {uploadLabel ?? t('receipt.upload.title')}
                </Text>
              </PlaceholderStyle>
            </DropZoneStyle>
          </RootStyle>

          <MaxSizeText variant="caption">
            <Trans
              i18nKey="receipt.max-file-size"
              components={{
                b: <br />,
              }}
            />
          </MaxSizeText>
          {allowWithoutImage && (
            <FormControlLabel
              sx={{
                mx: 'auto',
                mt: 2,
              }}
              control={
                <FCheckbox
                  formik={formik}
                  name="receipt_not_available"
                  disabled={disabled}
                />
              }
              label={t('receipt.upload.withoutImage')}
            />
          )}
        </Stack>
      )}
    </Card>
  );
};

interface UploadReceiptHookProps {
  formik: FormikProps<any>;
  /**
   * Upload target for the picked file. Defaults to the receipt bucket. Pass
   * this to store the file elsewhere, e.g. `useUploadMandateDocument`.
   */
  uploadFile?: { upload: UploadFile; uploadState: UploadState | undefined };
}

interface UploadReceiptHookReturn {
  file: any;
  setFile: (file: any) => void;
  uploadState?: UploadState;
  canDrop: boolean;
  isOver: boolean;
  drop: ConnectDropTarget;
  upload: UploadFile;
}

export const useUploadReceipt = ({
  formik,
  uploadFile,
}: UploadReceiptHookProps): UploadReceiptHookReturn => {
  const receiptUpload = useUploadReceiptImage(v4, computeMd5HashOfFile);
  const { upload, uploadState } = uploadFile ?? receiptUpload;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<any>(null);

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: any[] }) {
        if (setFile) {
          const file = item.files[0];
          if (!(file instanceof File)) {
            enqueueSnackbar(t('common.drop-file-from-computer'), {
              variant: 'error',
            });
            return;
          }
          const fileType = resolveFileType(file);
          if (!fileType) {
            enqueueSnackbar(t('receipt.upload.unsupported-file-type'), {
              variant: 'error',
            });
            return;
          }
          setFile({ preview: URL.createObjectURL(file) });
          formik.setSubmitting(true);
          upload(file, fileType);

          return;
        }
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
    }),
    [enqueueSnackbar, t]
  );

  return {
    file,
    setFile,
    upload,
    uploadState,
    canDrop,
    isOver,
    drop,
  };
};

const RootStyle = styled('div')(({ theme }: { theme: Theme }) => ({
  width: 144,
  height: 144,
  marginLeft: 'auto',
  marginRight: 'auto',
  borderRadius: '50%',
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
}));

export const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '50%',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9,
    },
  },
});

interface PlaceholderProps {
  hide: boolean;
}

export const PlaceholderStyle = styled('div')<PlaceholderProps>(
  ({ theme, hide }) => ({
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.neutral,
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': { opacity: 0.72 },
    ...(hide && {
      opacity: 0,
      color: 'common.white',
      bgcolor: 'grey.900',
      '&:hover': { opacity: 0.72 },
    }),
  })
);

interface FileChosenContainerProps {
  center?: boolean;
}

const FileChosenContainer = styled(Card)<FileChosenContainerProps>(
  ({ center }) =>
    ({ theme }) =>
      theme.unstable_sx({
        p: 1,
        flex: 1,
        display: 'flex',
        overflow: 'visible',
        // minWidth 0 keeps the thumbnail row's overflowX from widening the
        // whole card instead of scrolling; stretch lets the preview claim the
        // full height where the parent provides one (ENG-2295).
        minWidth: 0,
        minHeight: 0,
        alignItems: center ? 'center' : 'stretch',
      })
);

const MaxSizeText = styled(Text)(({ theme }) =>
  theme.unstable_sx({
    mt: 2,
    mx: 'auto',
    display: 'block',
    textAlign: 'center',
    color: 'text.secondary',
  })
);
